import {Component} from 'panel';
import isEmpty from 'lodash/isEmpty';
import differenceBy from 'lodash/differenceBy';
import groupBy from 'lodash/groupBy';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';

import {KEY_VALUES} from '../../util/dom';
import {registerMPElement} from '../../util/register-element';
import {fuzzyMatch} from '../../util/fuzzy-match';
import template from './index.jade';
import css from './index.styl?inline';

const YOUR_DASHBOARDS_SECTION = `Your dashboards`;
const ALL_DASHBOARDS_SECTION = `All dashboards`;

export default registerMPElement(
  `mp-dashboard-selector`,
  class extends Component {
    static get attrsSchema() {
      return {
        ...super.attrsSchema,
        'disable-create-dashboard': `boolean`,
        'disable-deselect-public': `boolean`,
        'enable-management': `boolean`,
        multiselect: `boolean`,
        open: `boolean`,
        'user-id': {type: `number`, description: `User ID of the user viewing selector`},
      };
    }

    constructor() {
      super(...arguments);
      this._controllerListener = this.updateFromController.bind(this);
    }

    get config() {
      return {
        css,
        template,
        useShadowDom: true,
        defaultState: {
          activeItem: null, // active means highlighted, triggered by mouseover or arrow keys
          busy: false,
          creating: false,
          dashboards: [],
          disableCreateDashboard: false,
          disableDeselectPublic: false,
          loaded: false,
          newTitle: ``,
          open: false,
          scrollToId: null,
          searchFilter: ``,
          sections: [],
          selectionChanges: new Set(),
        },
        helpers: {
          handleMenuChange: async (ev) => {
            ev.stopPropagation();
            this.dispatchEvent(new CustomEvent(`menuchange`, {detail: ev.detail}));

            if (ev.detail.state !== `closed`) {
              // menu opening
              this.focusSearch();
              if (this.controller && !this.controller.getLoaded()) {
                await this.controller.loadDashboards();
              }
            } else {
              // menu closing
              this.reset();
            }
          },
          handleSearchInput: (ev) => {
            const sections = this.getSections(this.state.dashboards, ev.target.value);
            this.update({
              searchFilter: ev.target.value,
              sections,
            });
            this.activateItemIfNeeded(sections);
          },
          handleSearchInputInsertHook: (vnode) => {
            this.searchInputEl = vnode.elm;
          },
          handleCreateInputInsertHook: (vnode) => {
            this.createInputEl = vnode.elm;
          },
          handleManageClick: () => this.dispatchEvent(new CustomEvent(`manage`)),
          handleCreateClick: () => {
            this.update({creating: true, newTitle: this.state.searchFilter});
            this.focusCreateField();
          },
          handleCreateInput: (ev) => {
            this.update({newTitle: ev.target.value});
          },
          handleCreateSubmit: async () => {
            const {newTitle} = this.state;
            if (!isEmpty(newTitle)) {
              this.update({busy: true});
              const dashboard = await this.controller.createDashboard({
                title: newTitle,
                fields: {
                  is_private: true, // eslint-disable-line camelcase
                },
              });
              this.dispatchEvent(new CustomEvent(`create`, {detail: dashboard}));
              this.update({busy: false, creating: false});
            }
          },
          handleApplyClick: async () => {
            const {dashboards, selectionChanges} = this.state;
            const changes = {
              added: dashboards
                .filter(
                  (dashboard) =>
                    selectionChanges.has(dashboard.id) && !this.controller.bookmarkContainsDashboardId(dashboard.id),
                )
                .map((dashboard) => dashboard.id),
              removed: dashboards
                .filter(
                  (dashboard) =>
                    selectionChanges.has(dashboard.id) && this.controller.bookmarkContainsDashboardId(dashboard.id),
                )
                .map((dashboard) => dashboard.id),
            };
            this.dispatchEvent(new CustomEvent(`multiselect`, {detail: changes}));
            await this.controller.changeBookmarkDashboards(changes);
            this.update({selectionChanges: new Set()});
          },
          handleItemRowInsertHook: (vnode) => {
            const itemRowEl = vnode.elm;
            if (itemRowEl.offsetParent && vnode.key === this.state.scrollToId) {
              this.update({scrollToId: null});
              window.requestAnimationFrame(() => {
                itemRowEl.offsetParent.scrollTop =
                  itemRowEl.offsetTop - itemRowEl.offsetParent.clientHeight + itemRowEl.offsetHeight;
              });
            }
          },
          getChecked: (item) => {
            const hasBookmark = this.controller.bookmarkContainsDashboardId(item.dashboard.id);
            return this.state.selectionChanges.has(item.dashboard.id) ? !hasBookmark : hasBookmark;
          },
          getCheckDisabled: (item) =>
            this.state.disableDeselectPublic &&
            this.controller.bookmarkContainsDashboardId(item.dashboard.id) &&
            !item.dashboard.is_private,
          getItemActionHandler: (item) => {
            if (this.state.multiselect) {
              if (this.helpers.getCheckDisabled(item)) {
                return () => {};
              } else {
                return () => {
                  const {selectionChanges} = this.state;
                  if (selectionChanges.has(item.dashboard.id)) {
                    selectionChanges.delete(item.dashboard.id);
                  } else {
                    selectionChanges.add(item.dashboard.id);
                  }
                  this.update({selectionChanges});
                  if (this.helpers.getChecked(item)) {
                    this.dispatchEvent(
                      new CustomEvent(`dashboardadded`, {detail: this.getVisibilityTrackingParams(item.dashboard)}),
                    );
                  }
                };
              }
            } else {
              return async () => {
                this.dispatchEvent(
                  new CustomEvent(`select`, {detail: this.getVisibilityTrackingParams(item.dashboard)}),
                );
                await this.controller.changeActiveDashboard(item.dashboard.id);
                this.dispatchEvent(new CustomEvent(`dashboardchanged`, {detail: {id: item.dashboard.id}}));
              };
            }
          },
          getItemMouseOverHandler: (item) => {
            return () => this.update({activeItem: item});
          },
        },
      };
    }

    getVisibilityTrackingParams(dashboard) {
      return {
        id: dashboard.id,
        userId: dashboard.creator_id,
        isVisible: !dashboard.is_private,
        isEditable: !dashboard.is_restricted,
      };
    }

    attributeChangedCallback(attr, oldVal, newVal) {
      super.attributeChangedCallback(attr, oldVal, newVal);
      switch (attr) {
        case `multiselect`:
        case `open`:
          this.update({[attr]: this.attr(attr)});
          break;
        case `disable-deselect-public`:
          this.update({disableDeselectPublic: this.attr(attr)});
          break;
        case `disable-create-dashboard`:
          this.update({disableCreateDashboard: this.attr(attr)});
          break;
      }
    }

    scrollActiveItemIntoView() {
      const itemRowEl = this.el.querySelector(`.mp-dashboard-selector-active`);
      const bottomScrollOffset = itemRowEl.offsetTop - itemRowEl.offsetParent.clientHeight + itemRowEl.offsetHeight;
      if (itemRowEl.offsetParent.scrollTop > itemRowEl.offsetTop) {
        itemRowEl.offsetParent.scrollTop = itemRowEl.offsetTop;
      } else if (itemRowEl.offsetParent.scrollTop < bottomScrollOffset) {
        itemRowEl.offsetParent.scrollTop = bottomScrollOffset;
      }
    }

    keyNavigateActiveItem(key) {
      const {activeItem, sections} = this.state;
      if (activeItem) {
        const sectionIdx = sections.findIndex((s) => s.items.some((it) => it.dashboard.id === activeItem.dashboard.id));
        const itemIdx = sections[sectionIdx].items.findIndex((it) => it.dashboard.id === activeItem.dashboard.id);

        let newActiveItem = null;
        switch (key) {
          case KEY_VALUES.ARROW_UP: {
            newActiveItem =
              sections[sectionIdx].items[itemIdx - 1] || // previous item in section
              (sectionIdx > 0 && [...sections[sectionIdx - 1].items].pop()) || // or last item in previous section
              null;
            break;
          }
          case KEY_VALUES.ARROW_DOWN: {
            newActiveItem =
              sections[sectionIdx].items[itemIdx + 1] || // next item in section
              (sectionIdx < sections.length - 1 && sections[sectionIdx + 1].items[0]) || // or first item in next section
              null;
            break;
          }
          default: {
            throw new Error(`Invalid navigation key`);
          }
        }
        if (newActiveItem) {
          this.update({activeItem: newActiveItem});
        }
      } else {
        this.update({activeItem: sections[0].items[0]});
      }
      window.requestAnimationFrame(() => this.scrollActiveItemIntoView());
    }

    connectedCallback() {
      super.connectedCallback(...arguments);
      this._handleKeyDown = (ev) => {
        switch (ev.key) {
          case KEY_VALUES.ARROW_DOWN:
          case KEY_VALUES.ARROW_UP: {
            ev.preventDefault();
            this.keyNavigateActiveItem(ev.key);
            window.requestAnimationFrame(() => this.scrollActiveItemIntoView());
            break;
          }
          case KEY_VALUES.ENTER: {
            if (this.state.activeItem) {
              this.helpers.getItemActionHandler(this.state.activeItem)();
            }
            break;
          }
        }
      };
      this.addEventListener(`keydown`, this._handleKeyDown);
    }

    disconnectedCallback() {
      this.removeEventListener(`keydown`, this._handleKeyDown);
      this.controller = null; // trigger unsubscribe in controller setter
      super.disconnectedCallback(...arguments);
    }

    reset() {
      this.update(
        pick(this.config.defaultState, [`activeItem`, `creating`, `newTitle`, `searchFilter`, `selectionChanges`]),
      );
      this.updateFromController();
    }

    focusSearch() {
      window.requestAnimationFrame(() => this.searchInputEl.focus());
    }

    focusCreateField() {
      window.requestAnimationFrame(() => {
        this.createInputEl.focus();
        this.createInputEl.select();
      });
    }

    getDashboardSection(dashboard) {
      if (this.isYourDashboard(dashboard)) {
        return YOUR_DASHBOARDS_SECTION;
      } else {
        return ALL_DASHBOARDS_SECTION;
      }
    }

    getSectionIndex(section) {
      switch (section) {
        case YOUR_DASHBOARDS_SECTION:
          return 1;
        case ALL_DASHBOARDS_SECTION:
          return 2;
        default:
          return Number.MAX_VALUE;
      }
    }

    getSections(dashboards, searchFilter) {
      dashboards = dashboards.filter((dashboard) => this.isYourDashboard(dashboard) || !dashboard.is_private);
      const items = dashboards
        .map((dashboard) => {
          const titleMatch = fuzzyMatch(dashboard.title, searchFilter) || {matchParts: null, score: 0};
          const descMatch = fuzzyMatch(dashboard.description, searchFilter) || {matchParts: null, score: 0};

          return {
            titleSearchTokens: titleMatch.matchParts,
            descriptionSearchTokens: descMatch.matchParts,
            matchScore: Math.max(titleMatch.score, descMatch.score),
            dashboard,
          };
        })
        .filter((item) => item.titleSearchTokens || item.descriptionSearchTokens)
        .sort((a, b) => b.matchScore - a.matchScore);

      const sectionMap = groupBy(items, (item) => this.getDashboardSection(item.dashboard));

      return sortBy(Object.entries(sectionMap), ([section]) => this.getSectionIndex(section))
        .map(([section, items]) => ({
          title: section,
          items,
        }))
        .filter((section) => section.items.length > 0);
    }

    isYourDashboard(dashboard) {
      return dashboard.creator_id === this.attr(`user-id`);
    }

    activateItemIfNeeded(sections) {
      const {activeItem} = this.state;
      const hasActiveItem =
        activeItem && sections.some((s) => s.items.some((it) => it.dashboard.id === activeItem.dashboard.id));
      if (!hasActiveItem) {
        this.update({activeItem: sections.length > 0 ? sections[0].items[0] : null});
      }
    }

    updateFromController() {
      const dashboards = this.controller.getDashboards();
      const added = differenceBy(dashboards, this.state.dashboards, `id`);
      if (this.state.dashboards && added.length > 0) {
        this.update({scrollToId: added[0].id});
      }

      const sections = this.getSections(dashboards || [], this.state.searchFilter);
      this.update({
        dashboards,
        sections,
      });
      this.activateItemIfNeeded(sections);
    }

    get controller() {
      return this.state.controller;
    }

    set controller(value) {
      if (this.state.controller) {
        this.unregisterController(this.state.controller);
      }
      this.update({controller: value});
      if (value) {
        this.registerController(value);
        this.updateFromController();
      }
    }

    registerController(controller) {
      controller.subscribeUpdates(this._controllerListener);
    }

    unregisterController(controller) {
      controller.unsubscribeUpdates(this._controllerListener);
    }
  },
);
